import React, { PropTypes } from 'react';
import Immutable from 'immutable';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { FormattedMessage } from 'react-intl';
import FullScreenOverlay from '@bibliocommons/base-full-screen-overlay';
import BcReactComponent from '@bibliocommons/base-bc-react-component';
import { Table, Placeholder } from '@bibliocommons/base-table';
import { GenericForm } from '@bibliocommons/shared-messages';
import { Checkmark, Failure } from '@bibliocommons/base-icons';
import { TransactionalButton } from '@bibliocommons/base-buttons';
import PickupBranchSelect from '@bibliocommons/shared-pickup-branch-select';
import ProgressBar from '@bibliocommons/shared-progress-bar';

import MessagingContainer from '../../../../../../containers/shared/Messaging/MessagingContainer';
import MessagingActions from '../../../../../../actions/MessagingActions';
import ItemLevelHoldsWorkflowTitle from './ItemLevelHoldsWorkflowTitle.component';

import '../styles/ItemLevelHoldsWorkflowView.scss';


class ItemLevelHoldsWorkflowView extends BcReactComponent {
  constructor(props) {
    super(props);

    this.state = {
      pickupLocation: props.branches.find(b => b.get('code') === props.preferredBranch) || Immutable.Map(),
      formValid: false
    };

    this.bindAll('handleSubmit', 'handleUpdatePickupLocation', 'renderInputControl', 'clearAllItemMessages', 'validateForm', 'renderItemLevelHoldsTable');
  }

  componentWillMount() {
    this.props.fetchItemGroups(this.props.bib.get('id'));
  }

  handleSubmit(e) {
    e.preventDefault();
    this.clearAllItemMessages();
    const itemIds = this.getSelectedItems();
    const { messagingId, workflowId } = this.props;
    this.props.placeItemHold({
      metadataId: this.props.bib.get('id'),
      itemIds,
      branchId: this.props.selectedBranch || this.state.pickupLocation.get('code'),
      messagingId,
      workflowId
    });
  }

  handleUpdatePickupLocation(pickupLocation) {
    this.setState({
      pickupLocation
    });
  }

  validateForm() {
    this.setState({
      formValid: this.getSelectedItems().length > 0
    });
  }

  getSelectedItems() {
    return Array.prototype.slice.call(this._form.elements)
      .filter((el) => el.checked)
      .map((el) => el.value);
  }

  renderInputControl(value, index) {
    if (this.props.failedItemIds.includes(value.toString())) {
      return <Failure color="#cf000f" />;
    } else if (this.props.successfulItemIds.includes(value.toString())) {
      return <Checkmark color="#008000" />;
    }

    const disabled = this.props.itemIdsOnHold.includes(value);
    return <input type={this.props.supportsBatchVolumeHolds ? 'checkbox' : 'radio'} onChange={this.validateForm} name="itemId" id={`itemGroup-${index}`} value={value} disabled={disabled} />;
  }

  static renderInputLabel(value, index) {
    return <label className="collection" htmlFor={`itemGroup-${index}`}>{value}</label>;
  }

  clearAllItemMessages() {
    const itemIds = this.props.itemGroups.map((group) => group.get('itemId')).toArray();
    this.props.dispatch(
      MessagingActions.clearMessages(itemIds)
    );
  }

  renderItemLevelHoldsTable(fields) {
    const { itemGroups, isFetchingItemGroups } = this.props;
    if (isFetchingItemGroups) {
      return <Placeholder />;
    }

    return (
      <Table
        className="table-hover"
        rows={itemGroups}
        fields={fields.filter(Boolean)} />
    );
  }

  render() {
    const { handleClose, bib, branches, isLoading, fieldErrors, itemGroups, showVolumeSelectorCollection, showVolumeSelectorStatus, showVolumeSelectorDueDate, isFetchingItemGroups } = this.props;
    const bibTitle = bib.get('title');
    const fields = [
      { key: 'itemId', type: 'itemId', transformationMap: this.renderInputControl, headerKey: 'table_headings_item_select' },
      (showVolumeSelectorCollection && { key: 'collection', type: 'collection', transformationMap: ItemLevelHoldsWorkflowView.renderInputLabel }),
      { key: 'callNumber', type: 'callNumber' },
      { key: 'volume', type: 'volume' },
      { key: 'copy', type: 'copy' },
      (showVolumeSelectorStatus && { key: 'availability.libraryStatus', type: 'String' }),
      (showVolumeSelectorDueDate && { key: 'dueDate', type: 'Date' })
    ];

    return (
      <FullScreenOverlay open handleClose={handleClose}>
        <ProgressBar active={isFetchingItemGroups} />
        <div className="cp-item-level-holds-workflow-view">
          <ItemLevelHoldsWorkflowTitle itemGroups={itemGroups} bibTitle={bibTitle} />
          <form action="" onSubmit={this.handleSubmit} ref={(el) => { this._form = el; }}>
            <div className="form-group clearfix">
              { !this.props.selectedBranch &&
                <PickupBranchSelect
                  fieldErrors={fieldErrors}
                  branches={branches}
                  label="Pickup at"
                  handleSelect={this.handleUpdatePickupLocation}
                  selectedBranch={this.state.pickupLocation.get('code')} /> }
            </div>
            { this.renderItemLevelHoldsTable(fields) }
            {
              itemGroups.map((group) => (
                <MessagingContainer id={group.get('itemId')}>
                  {
                    (messageOptions) => <GenericForm {...messageOptions} values={messageOptions.message.get('values', Immutable.Map()).merge({ title: bibTitle })} />
                  }
                </MessagingContainer>
              ))
            }

            <MessagingContainer id={bib.get('id')}>
              {
                (messageOptions) => <GenericForm {...messageOptions} values={messageOptions.message.get('values', Immutable.Map()).merge({ title: bibTitle })} />
              }
            </MessagingContainer>
            { !isFetchingItemGroups &&
              (<TransactionalButton isLoading={isLoading} type="submit" disabled={!this.state.formValid}>
                <FormattedMessage id="button_confirm" />
              </TransactionalButton>)
            }
            {
              !isFetchingItemGroups && !this.state.formValid && <span className="form-error"><FormattedMessage id="ilh_required_items" /></span>
            }
          </form>
        </div>
      </FullScreenOverlay>
    );
  }
}

ItemLevelHoldsWorkflowView.displayName = 'ItemLevelHoldsWorkflowView';
ItemLevelHoldsWorkflowView.propTypes = {
  messagingId: PropTypes.string,
  workflowId: PropTypes.string,
  bib: ImmutablePropTypes.map,
  placeItemHold: PropTypes.func.isRequired,
  fetchItemGroups: PropTypes.func.isRequired,
  selectedBranch: PropTypes.string,
  preferredBranch: PropTypes.string,
  handleClose: PropTypes.func.isRequired,
  fieldErrors: ImmutablePropTypes.map,
  isLoading: PropTypes.bool,
  branches: ImmutablePropTypes.listOf(
    ImmutablePropTypes.mapContains({
      code: PropTypes.string,
      name: PropTypes.string
    })
  ),
  itemGroups: ImmutablePropTypes.listOf(
    ImmutablePropTypes.mapContains({
      volume: PropTypes.string,
      collection: PropTypes.string.isRequired,
      copy: PropTypes.string,
      itemId: PropTypes.string.isRequired,
      callNumber: PropTypes.string
    })
  ),
  dispatch: PropTypes.func.isRequired,
  itemIdsOnHold: ImmutablePropTypes.listOf(PropTypes.string),
  supportsBatchVolumeHolds: PropTypes.bool,
  failedItemIds: ImmutablePropTypes.listOf(PropTypes.number),
  successfulItemIds: ImmutablePropTypes.listOf(PropTypes.number),
  showVolumeSelectorCollection: PropTypes.bool,
  showVolumeSelectorStatus: PropTypes.bool,
  showVolumeSelectorDueDate: PropTypes.bool,
  isFetchingItemGroups: PropTypes.bool
};

export default ItemLevelHoldsWorkflowView;



// WEBPACK FOOTER //
// ./app/components/shared/ItemTransactions/components/item-level-holds-workflow-view/components/ItemLevelHoldsWorkflowView.component.jsx